/**
 * Contracts module API — Calculator (plan §10.5, Appendix A "Calculator",
 * §13.8 WP-73; R31). Typed calls over the shared axios instance
 * (`services/api`), the same convention `api/fees.ts` / `api/notes.ts`
 * use — never axios directly (this repo's jest cannot parse axios's ESM
 * build unmocked).
 *
 * A new, SEPARATE file rather than an addition to `api/client.ts`: another
 * WP appends to that file concurrently, and this WP's brief forbids editing
 * it outright (`calculator.mount.md` step 4 is an optional later
 * re-export — `CalculatorPanel.tsx` never depends on it).
 *
 * Types mirror `backend/src/services/contracts/calculator/compute.js`'s
 * output shape (`{ value, label }` per key — see that file's README) and
 * `service.js#buildCalculatorInputs`'s `{ inputs, sources, warnings }`.
 */
import api from '../../services/api';

/** compute.js's G8 output label — never 'extracted' (that label belongs to an input, not an output). */
export type CalculatorOutputLabel = 'derived' | 'inferred';

/** compute.js FORMULAS's `{ value, label }` shape, for every scalar output. */
export interface CalculatorOutputValue<T = number> {
  value: T;
  label: CalculatorOutputLabel;
}

export type OverheadMode = 'line' | 'subtotal' | 'global' | 'embedded' | 'none' | 'unknown';
export type PaymentFrequency = 'monthly' | 'quarterly';
export type FeeTiming = 'start_up' | 'close_out' | 'monthly';
export type ScreenFailLimitedBy = 'none' | 'ratio' | 'cap' | 'both';

export interface CalculatorFeeInput {
  label?: string | null;
  amount: number;
  quantity?: number;
  timing?: FeeTiming;
}

export interface CalculatorInvoiceableInput {
  label?: string | null;
  amount: number;
  expected_quantity?: number;
}

export interface CalculatorScreenFailTier {
  up_to: number;
  amount: number;
}

/** §10.5 FACTS 3 — every arithmetic calculator input, all optional here (the panel fills defaults visually; the backend fills them for real). */
export interface CalculatorInputs {
  enrollment_target?: number;
  screen_fail_rate?: number;
  visits_attended_fraction?: number;
  assume_all_visits_attended?: boolean;
  assume_invoiceables_done?: boolean;
  per_patient_total?: number | null;
  overhead_pct?: number;
  overhead_mode?: OverheadMode;
  screening_visit_cost?: number | null;
  screen_fail_ratio?: number | null;
  screen_fail_cap?: number | null;
  screen_fail_pct?: number;
  screen_fail_tiers?: CalculatorScreenFailTier[] | null;
  fees?: CalculatorFeeInput[];
  invoiceables?: CalculatorInvoiceableInput[];
  holdback_pct?: number;
  holdback_release_trigger?: string | null;
  holdback_release_month?: number;
  payment_frequency?: PaymentFrequency;
  payment_days?: number;
  study_months?: number;
  enrollment_months?: number;
}

export interface CashFlowRow {
  month: number;
  earned: number;
  invoiced: number;
  holdback_withheld: number;
  paid: number;
  cumulative_paid: number;
  holdback_release?: number;
  holdback_release_trigger?: string | null;
}

/** runCalculator()'s whole return — every §10.5 output, `{ value, label }` per key. */
export interface CalculatorOutputs {
  per_patient_revenue_per_subject: CalculatorOutputValue;
  per_patient_revenue: CalculatorOutputValue;
  subjects_screened: CalculatorOutputValue;
  screen_fails_expected: CalculatorOutputValue;
  screen_fail_ratio_limit: CalculatorOutputValue<number | null>;
  screen_fail_cap_limit: CalculatorOutputValue<number | null>;
  screen_fail_limited_by: CalculatorOutputValue<ScreenFailLimitedBy>;
  screen_fail_unit: CalculatorOutputValue;
  screen_fail_allowance: CalculatorOutputValue;
  fixed_fees: CalculatorOutputValue;
  invoiceables_total: CalculatorOutputValue;
  total_contract_value: CalculatorOutputValue;
  holdback_total: CalculatorOutputValue;
  cash_flow: CalculatorOutputValue<CashFlowRow[]>;
  assumptions: string[];
}

/** Which of the five precedence tiers each resolved input came from (service.js#buildCalculatorInputs). */
export type CalculatorInputSource = 'workbook_line' | 'clause_term' | 'negotiation' | 'override';

export interface RunCalculatorBody {
  negotiationId: string;
  versionId?: string;
  overrides?: CalculatorInputs;
}

export interface RunCalculatorResult {
  negotiation_id: string;
  study_id: string;
  version_id: string | null;
  inputs: CalculatorInputs;
  outputs: CalculatorOutputs;
  sources: Partial<Record<keyof CalculatorInputs, CalculatorInputSource>>;
  /** A plain array of reason codes (e.g. 'no_budget_version'); see the two below for the richer detail. */
  warnings: string[];
  /** Budget lines whose cost cell was text (e.g. 'INV') — excluded from every sum, never silently dropped (G9). */
  unpriced: string[];
  /** A CTA clause term whose written and digit forms disagreed — never used as a number (A.7, G3). */
  term_disagreements: Array<{ class: string; text: string | null }>;
}

/** POST /calculator/run — Appendix A. Writes nothing. */
export async function runCalculator(body: RunCalculatorBody): Promise<RunCalculatorResult> {
  const response = await api.post<RunCalculatorResult>('/api/contracts/calculator/run', body);
  return response.data;
}

export interface CalculatorScenario {
  id: string;
  study_id: string;
  negotiation_id: string | null;
  inputs: CalculatorInputs & { name: string; version_id: string | null; provenance: Record<string, unknown> };
  outputs: CalculatorOutputs;
  created_at: string;
  created_by: string;
}

/** GET /calculator/scenarios?negotiationId — Appendix A. Newest first. */
export async function listScenarios(negotiationId: string): Promise<CalculatorScenario[]> {
  const response = await api.get<{ count: number; scenarios: CalculatorScenario[] }>('/api/contracts/calculator/scenarios', {
    params: { negotiationId },
  });
  return response.data.scenarios;
}

export interface SaveScenarioBody {
  negotiationId: string;
  versionId?: string;
  name: string;
  overrides?: CalculatorInputs;
}

/** POST /calculator/scenarios — Appendix A. Outputs are always recomputed server-side (G11, G13). */
export async function saveScenario(body: SaveScenarioBody): Promise<CalculatorScenario> {
  const response = await api.post<{ scenario: CalculatorScenario }>('/api/contracts/calculator/scenarios', body);
  return response.data.scenario;
}
